import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import { LocalDateTime } from '@js-joda/core';
import { useQueryClient } from 'react-query';
import {
  BackfillRecipeForm,
  PourDto,
  PourForm,
  PourPresetDto,
  useAllProjectRecipesQuery,
  useCreateRecipeMutation,
  useGetPourPresetGroup
} from 'providers/api';
import useApi from 'providers/api/useApi';
import { useProject } from 'modules/project/providers/project';
import { useScenario } from 'modules/project/providers/scenario';
import { useConfirmation } from 'providers/confirm';
import {
  defaultFillAnchor,
  FillAnchor,
  BeachSurface,
  dischargeDatum,
  getFillCurve,
  LEGACY_BEACH_ANGLE_DEGREES,
  resolveNewPourBeachAngle,
  resolvePourBeachAngle,
  FillCurve
} from 'modules/project/StopeFillPage/Three/pour';
import { Model } from '../../hooks/useModelState';
import {
  buildAutoPourByPreset,
  buildAutoRecipeForm,
  findRecipeForPreset,
  getPresetSequence,
  getRecipeWarningCodes,
  makeAutoPourReference,
  plusCuringDays,
  PlannedDate,
  positiveNumber,
  stopeStartPourDate,
  zonedPourDateToLocal
} from '../../AutoPourForm/autoPourRecipe';
import { getStopeFillSceneState } from './stopeFillSceneState';

interface StopePresetPoursProps {
  model: Model;
  projectId: string;
  stopeId: string;
  existingPours: PourDto[];
  onChanged: () => void;
}

interface ResolvedVolume {
  volume: number;
  height?: number;
}

const AUTO_RECIPE = 'auto';

const normalizeRecipeIds = (result: string | string[] | undefined): string[] => {
  if (Array.isArray(result)) return result.filter(Boolean);
  return result ? [result] : [];
};

// The paste beach slope for a preset, in degrees from horizontal. Presets carry no angle of their own, so
// a paste preset takes the project default and a rock-fill preset short-circuits to a level 0. These are
// pours that do not exist yet, hence resolveNewPourBeachAngle rather than the existing-pour resolver.
const presetBeachAngle = (
  preset: { isRockFill?: boolean } | undefined,
  projectDefaultDegrees: number,
): number => resolveNewPourBeachAngle(undefined, projectDefaultDegrees, preset?.isRockFill);

// A "fill remaining" slot absorbs whatever height the fixed slots leave: its thickness is the stope's
// floor-to-ceiling reach AT THE DISCHARGE COLUMN minus the heights of every other fixed slot. Volume-defined
// slots are reserved against the remaining volume as a fallback.
const resolveFillRemaining = (
  curve: FillCurve,
  datum: number,
  otherPresets: PourPresetDto[],
  columnHeight: number,
): ResolvedVolume | null => {
  const fixed = otherPresets.filter((preset) => !preset.fillRemainingHeight);
  const reservedHeight = fixed.reduce((sum, preset) => sum + (positiveNumber(preset.height) ?? 0), 0);
  const reservedVolume = fixed.reduce(
    (sum, preset) => sum + (positiveNumber(preset.height) ? 0 : (positiveNumber(preset.volume) ?? 0)),
    0,
  );

  const remainingVolume = Math.max(0, curve.totalVolume - curve.baseVolume);

  if (reservedHeight > 0 && Number.isFinite(columnHeight)) {
    const fillHeight = columnHeight - reservedHeight;
    if (fillHeight <= 0) return null;
    const heightVolume = Math.max(0, curve.volumeAt(datum + fillHeight) - curve.baseVolume);
    if (reservedVolume <= 0) {
      return heightVolume > 0 ? { volume: heightVolume, height: fillHeight } : null;
    }
    const target = Math.min(heightVolume, remainingVolume - reservedVolume);
    return target > 0 ? { volume: target } : null;
  }

  if (reservedVolume > 0) {
    const target = remainingVolume - reservedVolume;
    return target > 0 ? { volume: target } : null;
  }

  return remainingVolume > 0 ? { volume: remainingVolume } : null;
};

const resolvePresetVolume = (
  preset: PourPresetDto,
  curve: FillCurve,
  datum: number,
  allPresets: PourPresetDto[],
  columnHeight: number,
): ResolvedVolume | null => {
  if (preset.fillRemainingHeight) {
    const otherPresets = allPresets.filter((candidate) => candidate.entityId !== preset.entityId);
    return resolveFillRemaining(curve, datum, otherPresets, columnHeight);
  }

  const height = positiveNumber(preset.height);
  if (height) {
    const volume = Math.max(0, curve.volumeAt(datum + height) - curve.baseVolume);
    return volume > 0 ? { volume, height } : null;
  }

  const volume = positiveNumber(preset.volume);
  return volume ? { volume } : null;
};

const StopePresetPours = ({ model, projectId, stopeId, existingPours, onChanged }: StopePresetPoursProps) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const confirm = useConfirmation();
  const { activeScenarioId } = useScenario();
  const { state: { timeZone } } = useProject();
  const createRecipeMutation = useCreateRecipeMutation();

  const [busyPresetId, setBusyPresetId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // Per-preset recipe choice: AUTO_RECIPE (build/reuse the preset's auto recipe) or a specific recipe id.
  const [recipeChoiceByPreset, setRecipeChoiceByPreset] = React.useState<Record<string, string>>({});

  const stope = model.selectedStope;
  const groupId = stope?.data?.pourPresetGroupId;
  // Preset pours have no per-pour override UI, so they take the project default (D5).
  const projectDefaultBeachAngle = model.project?.defaultPasteBeachAngleDegrees ?? LEGACY_BEACH_ANGLE_DEGREES;

  const { data: group, isLoading: groupLoading } = useGetPourPresetGroup(groupId ?? '', { enabled: !!groupId });
  const { data: allRecipes } = useAllProjectRecipesQuery({ projectId, scenarioId: activeScenarioId ?? undefined });

  const recipes = React.useMemo(() => allRecipes ?? [], [allRecipes]);
  // Non-stale recipes belonging to this stope — the pool a preset can be pointed at instead of auto-creating.
  const stopeRecipes = React.useMemo(
    () => recipes.filter((recipe) => recipe.stopeIdentifier?.stopeId === stopeId && !recipe.isStale),
    [recipes, stopeId],
  );
  const presets = React.useMemo(
    () => (stope && group ? getPresetSequence(stope, new Map(), group) ?? [] : []),
    [stope, group],
  );
  const autoPoursByPreset = React.useMemo(
    () => buildAutoPourByPreset(existingPours, recipes),
    [existingPours, recipes],
  );

  const invalidatePourQueries = React.useCallback(async () => {
    await queryClient.invalidateQueries('pours', { exact: false });
    await queryClient.invalidateQueries('paged_pours', { exact: false });
    await queryClient.invalidateQueries('stopeBackfills', { exact: false });
    await queryClient.invalidateQueries('stopeBackfill', { exact: false });
    await queryClient.invalidateQueries('recipes', { exact: false });
  }, [queryClient]);

  // Resolves a stope+preset to a concrete recipe id. If the user picked a specific recipe for the preset we
  // use it directly; otherwise (Auto-create) we reuse the existing auto recipe for that preset and only
  // create one when none exists. The auto path reads the recipe list fresh from the API rather than the
  // cached query snapshot, because a recipe created moments earlier (in a prior create/regenerate) may not
  // have propagated into the snapshot yet — which would otherwise spawn a duplicate recipe every time.
  const resolveRecipe = React.useCallback(async (
    preset: PourPresetDto,
  ): Promise<{ recipeId: string; acceptedWarnings: string[] } | null> => {
    if (!stope) return null;

    const choice = recipeChoiceByPreset[preset.entityId];
    if (choice && choice !== AUTO_RECIPE) {
      const chosen = stopeRecipes.find((recipe) => recipe.entityId === choice);
      if (chosen?.entityId) {
        return { recipeId: chosen.entityId, acceptedWarnings: getRecipeWarningCodes(chosen) };
      }
    }

    const latestRecipes = await api.recipes.getAllByProject(projectId, activeScenarioId ?? undefined);
    const existing = findRecipeForPreset(latestRecipes, stopeId, preset.entityId);
    if (existing?.entityId) {
      return { recipeId: existing.entityId, acceptedWarnings: getRecipeWarningCodes(existing) };
    }

    const form: BackfillRecipeForm | null = buildAutoRecipeForm(model, stope, preset);
    if (!form) return null;

    const created = await createRecipeMutation.mutateAsync({
      projectId,
      data: [form],
      context: { stopeName: [stope.data.stopeName] },
    });
    const recipeId = normalizeRecipeIds(created as unknown as string | string[] | undefined)[0];
    if (!recipeId) return null;
    const acceptedWarnings = (form.warnings?.map((warning) => warning.code).filter(Boolean) as string[]) ?? [];
    return { recipeId, acceptedWarnings };
  }, [activeScenarioId, api, createRecipeMutation, model, projectId, recipeChoiceByPreset, stope, stopeId, stopeRecipes]);

  // Creates a pour for every preset in `recreate`. Walks the presets bottom-up exactly as walkStopeFill
  // does, accumulating a list of BEACH SURFACES: each slot places its own cone, whether it is being
  // created now, is an existing pour we keep, or is an empty reserved slot. Every slot therefore lands on
  // top of what is below it. This is why regenerating a lower preset must recreate every preset above it.
  //
  // No CSG anywhere: an empty reserved slot used to need a boolean carve out of a working mesh, and now
  // just contributes one more surface.
  const simulateAndCreate = React.useCallback(async (
    recreate: Set<string>,
    deletedPourIds: Set<string>,
  ): Promise<number> => {
    if (!stope) return 0;
    const base = getStopeFillSceneState().originalStopeModel;
    if (!base) {
      throw new Error(i18n._('The stope shape is still loading. Please try again in a moment.'));
    }

    const { pipePosition } = getStopeFillSceneState();
    const anchor: FillAnchor = pipePosition
      ? { x: pipePosition.x, z: pipePosition.y }
      : defaultFillAnchor(base);

    // The empty-stope curve: used ONLY for the floor-to-ceiling reach, a property of the stope rather
    // than of any one slot.
    const columnCurve = getFillCurve(base, [], anchor, projectDefaultBeachAngle);
    const dischargeFloorLevel = dischargeDatum(base, [], anchor);

    // Floor-to-ceiling reach of the stope at the discharge column — what the stacked slot heights must sum
    // to. Taken from the curve rather than a downward ray, so it cannot be missed by a probe that slips
    // through a thin feature.
    const columnHeight = columnCurve.maxLevel - dischargeFloorLevel;

    // Each preset owns a fixed slot in the column. The fill-remaining slot absorbs whatever the fixed slots
    // leave; a slot with neither a height nor a fill-remaining flag is volume-only.
    const fixedReservedHeight = presets.reduce((sum, preset) => sum + (positiveNumber(preset.height) ?? 0), 0);
    const slotHeight = (preset: PourPresetDto): number | null => {
      if (preset.fillRemainingHeight) {
        const fill = columnHeight - fixedReservedHeight;
        return fill > 0 ? fill : null;
      }
      return positiveNumber(preset.height) ?? null;
    };

    // The beaches placed so far, in order - the same state walkStopeFill carries.
    const placed: BeachSurface[] = [];
    let runningDate: PlannedDate = stopeStartPourDate(stope) ?? LocalDateTime.now(timeZone);
    let created = 0;

    await presets.reduce(async (previous, preset) => {
      await previous;

      const existing = autoPoursByPreset.get(preset.entityId)?.pour;
      const keep = !!existing && !deletedPourIds.has(existing.entityId);
      const isRecreate = recreate.has(preset.entityId);
      const slot = slotHeight(preset);

      // Price this slot on ITS OWN angle's curve. A rock-fill preset is created with a level 0-degree
      // beach, so pricing it on the paste curve would hand it a volume that walkStopeFill immediately
      // disagrees with - hundreds of m3 on a real footprint, which the volume table would then flag as
      // stale and "Update Volumes" would rewrite the moment the pour was created.
      const slotAngle = presetBeachAngle(preset, projectDefaultBeachAngle);
      const slotCurve = getFillCurve(base, placed, anchor, slotAngle);

      // Where this slot starts: the fill surface already under the pipe, or the bare floor for the very
      // first slot. Identical to how walkStopeFill places a slice.
      const datum = dischargeDatum(base, placed, anchor);
      let slotVolume = slot != null
        ? Math.max(0, slotCurve.volumeAt(datum + slot) - slotCurve.baseVolume)
        : positiveNumber(preset.volume) ?? 0;
      let slotAngleUsed = slotAngle;
      let slotHeightUsed = slot;

      if (isRecreate) {
        const resolved = resolvePresetVolume(preset, slotCurve, datum, presets, columnHeight);
        if (resolved) {
          slotVolume = resolved.volume;
          slotHeightUsed = resolved.height ?? null;
          const basePourForm: PourForm = {
            reference: makeAutoPourReference(stope.data.stopeName, preset.name),
            plannedDate: runningDate,
            volume: resolved.volume,
            acceptedWarnings: [],
            pourPositionX: anchor.x,
            pourPositionY: anchor.z,
            pourHeight: resolved.height,
            pasteBeachAngleDegrees: slotAngle,
          };

          if (preset.isRockFill) {
            // Rock fill has no recipe — create a recipe-less, volume-only pour linked to its preset.
            await api.pours.createRockFillPour(projectId, stopeId, basePourForm, activeScenarioId ?? undefined, preset.entityId);
            created += 1;
          } else {
            const recipe = await resolveRecipe(preset);
            if (recipe) {
              await api.pours.create(recipe.recipeId, { ...basePourForm, acceptedWarnings: recipe.acceptedWarnings });
              created += 1;
            }
          }
        }
        runningDate = plusCuringDays(runningDate, preset.curingDays);
      } else if (keep && existing) {
        // An existing pour reserves the volume it actually holds, measured at the angle IT was planned at
        // rather than at this preset's angle.
        slotAngleUsed = resolvePourBeachAngle(existing);
        const existingCurve = getFillCurve(base, placed, anchor, slotAngleUsed);
        slotHeightUsed = existing.pourHeight ?? null;
        slotVolume = existing.pourHeight != null
          ? Math.max(0, existingCurve.volumeAt(datum + existing.pourHeight) - existingCurve.baseVolume)
          : existing.volume;
        runningDate = plusCuringDays(zonedPourDateToLocal(existing.intendedPourDate) ?? runningDate, preset.curingDays);
      } else {
        runningDate = plusCuringDays(runningDate, preset.curingDays);
      }

      // Place this slot's beach. An empty reserved slot places one too, so a slice created above it lands
      // on top of it rather than filling the gap below.
      const placedCurve = slotAngleUsed === slotAngle ? slotCurve : getFillCurve(base, placed, anchor, slotAngleUsed);
      placed.push({
        anchor,
        slopeDegrees: slotAngleUsed,
        level: slotHeightUsed != null
          ? datum + slotHeightUsed
          : placedCurve.levelAt(placedCurve.baseVolume + slotVolume),
      });
      return undefined;
    }, Promise.resolve<undefined>(undefined));

    return created;
  }, [activeScenarioId, api, autoPoursByPreset, presets, projectId, resolveRecipe, stope, stopeId, timeZone, projectDefaultBeachAngle]);

  const runAction = React.useCallback(async (presetId: string, action: () => Promise<void>) => {
    setBusyPresetId(presetId);
    setError(null);
    try {
      await action();
      await invalidatePourQueries();
      onChanged();
    } catch (err) {
      setError((err as Error)?.message ?? i18n._('The preset pour action failed.'));
    } finally {
      setBusyPresetId(null);
    }
  }, [invalidatePourQueries, onChanged]);

  const handleCreate = (preset: PourPresetDto) => {
    runAction(preset.entityId, async () => {
      await simulateAndCreate(new Set([preset.entityId]), new Set());
    });
  };

  const handleDelete = (preset: PourPresetDto) => {
    const existing = autoPoursByPreset.get(preset.entityId)?.pour;
    if (!existing?.entityId) return;
    confirm({
      variant: 'danger',
      description: i18n._('This will permanently delete this pour. Continue?'),
    }).then(() => {
      runAction(preset.entityId, async () => {
        await api.pours.deletePour(existing.entityId);
      });
    });
  };

  // Regenerating a preset deletes its pour and every pour stacked above it (later in the sequence), then
  // recreates the whole affected set so the volumes above reflect the regenerated pour's new shape.
  const handleRegenerate = (preset: PourPresetDto, index: number) => {
    const affected = presets
      .slice(index)
      .map((candidate) => ({ candidate, pour: autoPoursByPreset.get(candidate.entityId)?.pour }))
      .filter((entry) => entry.candidate.entityId === preset.entityId || !!entry.pour);

    const pourIdsToDelete = affected
      .map((entry) => entry.pour?.entityId)
      .filter((id): id is string => !!id);
    const recreateIds = new Set(affected.map((entry) => entry.candidate.entityId));
    const aboveCount = affected.length - 1;

    confirm({
      variant: 'danger',
      description: aboveCount > 0
        ? i18n._('Regenerating this pour will also regenerate {count} pour(s) stacked above it. Continue?', { count: aboveCount })
        : i18n._('This will regenerate this preset pour. Continue?'),
    }).then(() => {
      runAction(preset.entityId, async () => {
        if (pourIdsToDelete.length > 0) {
          await api.pours.deletePours({ pourIds: pourIdsToDelete });
        }
        await simulateAndCreate(recreateIds, new Set(pourIdsToDelete));
      });
    });
  };

  if (!groupId) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <Trans id="No pour preset group is assigned to this stope.">No pour preset group is assigned to this stope.</Trans>
        </Typography>
      </Box>
    );
  }

  if (groupLoading) {
    return (
      <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (presets.length === 0) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <Trans id="The assigned preset group has no presets.">The assigned preset group has no presets.</Trans>
        </Typography>
      </Box>
    );
  }

  const anyBusy = busyPresetId !== null;

  return (
    <Box>
      {group?.name && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {group.name}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Stack spacing={0.5}>
        {presets.map((preset, index) => {
          const existing = autoPoursByPreset.get(preset.entityId)?.pour;
          const busy = busyPresetId === preset.entityId;

          return (
            <Box
              key={preset.entityId}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                py: 0.5,
                px: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap title={preset.name}>{preset.name}</Typography>
                  {existing && (
                    <Chip size="small" color="success" variant="outlined" label={i18n._('Created')} />
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {busy && <CircularProgress size={16} />}
                  {existing ? (
                    <>
                      <Button
                        size="small"
                        color="secondary"
                        startIcon={<RefreshIcon fontSize="small" />}
                        disabled={anyBusy}
                        onClick={() => handleRegenerate(preset, index)}
                      >
                        <Trans id="Regenerate">Regenerate</Trans>
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineIcon fontSize="small" />}
                        disabled={anyBusy}
                        onClick={() => handleDelete(preset)}
                      >
                        <Trans id="Delete">Delete</Trans>
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      startIcon={<AddIcon fontSize="small" />}
                      disabled={anyBusy}
                      onClick={() => handleCreate(preset)}
                    >
                      <Trans id="Create">Create</Trans>
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Rock-fill presets have no recipe, so there is nothing to pick — show only the fill action. */}
              {!preset.isRockFill && (
                <TextField
                  select
                  size="small"
                  fullWidth
                  label={i18n._('Recipe')}
                  value={recipeChoiceByPreset[preset.entityId] ?? AUTO_RECIPE}
                  onChange={(event) => setRecipeChoiceByPreset(
                    (prev) => ({ ...prev, [preset.entityId]: event.target.value }),
                  )}
                  disabled={anyBusy}
                >
                  <MenuItem value={AUTO_RECIPE}>{i18n._('Auto-create recipe')}</MenuItem>
                  {stopeRecipes.map((recipe) => (
                    <MenuItem key={recipe.entityId} value={recipe.entityId}>{recipe.reference}</MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default StopePresetPours;
